import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Activity, Download, Database, Zap, StopCircle, Play } from 'lucide-react';
import { toast } from 'sonner';

const BASE = process.env.REACT_APP_BACKEND_URL || '';
const EVENT_COLORS = { pageview: '#1E5A8A', read_complete: '#1F7A5A', share: '#B89B5E', comment: '#9A6B1A', funnel: '#B3261E' };

export default function AdminAnalyticsStream() {
  const [events, setEvents] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [warehouse, setWarehouse] = useState(null);
  const [granularity, setGranularity] = useState('day');
  const [days, setDays] = useState(30);
  const [loadingWarehouse, setLoadingWarehouse] = useState(false);
  const esRef = useRef(null);

  useEffect(() => {
    loadPipelineStatus();
  }, []);

  const loadPipelineStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const r = await fetch(`${BASE}/api/analytics/pipeline-status`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setPipelineStatus(data);
    } catch {}
  };

  const startStream = () => {
    if (esRef.current) esRef.current.close();
    const token = localStorage.getItem('access_token');
    const es = new EventSource(`${BASE}/api/analytics/stream?token=${token}`);
    esRef.current = es;
    setStreaming(true);
    setEvents([]);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'connected') return;
        setEvents(prev => [data, ...prev.slice(0, 49)]);
      } catch {}
    };
    es.onerror = () => {
      setStreaming(false);
      es.close();
    };
  };

  const stopStream = () => {
    if (esRef.current) esRef.current.close();
    setStreaming(false);
  };

  useEffect(() => () => { if (esRef.current) esRef.current.close(); }, []);

  const loadWarehouse = async () => {
    setLoadingWarehouse(true);
    try {
      const token = localStorage.getItem('access_token');
      const r = await fetch(`${BASE}/api/analytics/warehouse?days=${days}&granularity=${granularity}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setWarehouse(data);
    } catch { toast.error('Failed to load warehouse'); }
    finally { setLoadingWarehouse(false); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${BASE}/api/analytics/export-csv?days=${days}&granularity=${granularity}&token=${token}`, '_blank');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Activity className="w-6 h-6 text-[#B89B5E]" /> Analytics Data Infrastructure</h1>
        <p className="text-sm text-[#5B616E] mt-1">Real-time event stream, data warehouse, CSV export pipeline</p>
      </div>

      {/* Pipeline status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Events / Hour', value: pipelineStatus?.events_last_hour?.toLocaleString() || '—', color: '#1E5A8A', icon: Zap },
          { label: 'Events / 24h', value: pipelineStatus?.events_last_24h?.toLocaleString() || '—', color: '#B89B5E', icon: Activity },
          { label: 'Total Events', value: pipelineStatus?.total_events?.toLocaleString() || '—', color: '#1F7A5A', icon: Database },
          { label: 'Pipeline', value: pipelineStatus?.pipeline_health || '—', color: pipelineStatus?.pipeline_health === 'healthy' ? '#1F7A5A' : '#9A6B1A', icon: Activity },
        ].map(k => (
          <Card key={k.label} className="bg-white border-[#E7E0D6]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1"><k.icon className="w-4 h-4" style={{ color: k.color }} /><span className="text-xs text-[#5B616E]">{k.label}</span></div>
              <p className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Live event stream */}
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${streaming ? 'bg-red-500 animate-pulse' : 'bg-[#E7E0D6]'}`} />
                Real-Time Event Stream (SSE)
              </CardTitle>
              {streaming
                ? <Button size="sm" variant="outline" onClick={stopStream} className="text-xs"><StopCircle className="w-3 h-3 mr-1" />Stop</Button>
                : <Button size="sm" onClick={startStream} className="text-xs bg-[#121316] text-white"><Play className="w-3 h-3 mr-1" />Start Stream</Button>}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5 max-h-72 overflow-y-auto font-mono text-[10px]">
              {events.length === 0 ? (
                <p className="text-[#5B616E] text-center py-6 font-sans text-sm">{streaming ? 'Waiting for events…' : 'Click "Start Stream" to begin live analytics'}</p>
              ) : events.map((e, i) => (
                <div key={i} className="flex items-center gap-2 py-0.5 border-b border-[#F0EDE6]">
                  <Badge style={{ background: `${EVENT_COLORS[e.type] || '#5B616E'}20`, color: EVENT_COLORS[e.type] || '#5B616E' }} className="text-[8px] px-1 py-0">{e.type}</Badge>
                  <span className="text-[#5B616E]">{e.country || '??'} · {e.device || '??'}</span>
                  {e.article_id && <span className="text-[#B89B5E]">art:{e.article_id.slice(-6)}</span>}
                  <span className="ml-auto text-[#E7E0D6]">{e.ts ? e.ts.slice(11, 19) : ''}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Data warehouse */}
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Database className="w-4 h-4" /> Analytics Warehouse</CardTitle>
              <div className="flex gap-1">
                <Select value={String(days)} onValueChange={v => setDays(parseInt(v))}>
                  <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{['7', '30', '90'].map(d => <SelectItem key={d} value={d}>{d}d</SelectItem>)}</SelectContent>
                </Select>
                <Select value={granularity} onValueChange={setGranularity}>
                  <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{['day', 'week', 'month'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Button size="sm" onClick={loadWarehouse} disabled={loadingWarehouse} className="flex-1 text-xs bg-[#121316] text-white">
                {loadingWarehouse ? 'Loading…' : 'Run Aggregation'}
              </Button>
              <Button size="sm" variant="outline" onClick={exportCsv} className="text-xs">
                <Download className="w-3 h-3 mr-1" /> CSV
              </Button>
            </div>
            {loadingWarehouse && <Skeleton className="h-40" />}
            {warehouse && !loadingWarehouse && (
              <>
                <p className="text-xs text-[#5B616E]">{warehouse.rows} rows · {warehouse.granularity} granularity · {warehouse.days} days</p>
                <div className="overflow-auto max-h-48 text-[10px] font-mono">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#FBF7EF]">
                        {['date', 'event_type', 'device', 'country', 'count', 'unique_articles'].map(h => (
                          <th key={h} className="px-1.5 py-1 text-left text-[9px] text-[#5B616E] uppercase border-b border-[#E7E0D6]">{h.replace('_', ' ')}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {warehouse.warehouse.slice(0, 20).map((row, i) => (
                        <tr key={i} className="border-b border-[#F0EDE6] hover:bg-[#FBF7EF]">
                          {['date', 'event_type', 'device', 'country', 'count', 'unique_articles'].map(k => (
                            <td key={k} className="px-1.5 py-0.5 text-[10px]">{String(row[k] ?? '—')}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {warehouse.rows > 20 && <p className="text-[9px] text-[#5B616E] mt-1 text-center">…{warehouse.rows - 20} more rows — export CSV for full data</p>}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
